import React from "react";
import { motion } from "framer-motion";
import { TrendingUp } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

const computeFallbackROI = (collections, patients) => {
  const scale = collections / 100000;
  const ptScale = patients / 300;
  const prodFactor = (scale * 0.4 + ptScale * 0.6);
  const prodC = Math.round(5000 * scale * (0.6 + ptScale * 0.4));
  const prodR = Math.round(8000 * scale * (0.6 + ptScale * 0.4));
  const ncrC = Math.round(collections * 0.01);
  const ncrR = Math.round(collections * 0.02);
  const staffFactor = (scale * 0.5 + ptScale * 0.5);
  const staffC = Math.round(2500 * staffFactor);
  const staffR = Math.round(3500 * staffFactor);
  const swScale = Math.max(1, 0.7 + scale * 0.3);
  const swC = Math.round(2000 * swScale);
  const swR = Math.round(3000 * swScale);
  const fteLow = Math.round(staffFactor * 0.33 * 100) / 100;
  const fteHigh = Math.round(staffFactor * 0.45 * 100) / 100;
  const newPtsLow = Math.max(2, Math.round(3 * prodFactor));
  const newPtsHigh = Math.max(3, Math.round(5 * prodFactor));
  const hygieneRecallsLow = Math.max(2, Math.round(3 * prodFactor));
  const hygieneRecallsHigh = Math.max(3, Math.round(5 * prodFactor));
  const caseAcceptanceLow = Math.max(1, Math.round(1 * prodFactor));
  const caseAcceptanceHigh = Math.max(1, Math.round(2 * prodFactor));
  return {
    rows: [
      { driver: "Additional production from AI agents", description: "New patients from after-hours booking, hygiene recall & reactivation, case acceptance follow-up, schedule optimization", assumptions: `${newPtsLow}-${newPtsHigh} new pts, ${hygieneRecallsLow}-${hygieneRecallsHigh} hygiene recalls, ${caseAcceptanceLow}-${caseAcceptanceHigh} case acceptance conversions/mo`, conservative: prodC, realistic: prodR },
      { driver: "Net collection rate improvement", description: "AI claim scrubbing reduces denials, automated follow-up recovers aging AR, faster EOB posting and payment reconciliation", assumptions: `1-2% NCR improvement on $${(collections / 1000).toFixed(0)}K collections`, conservative: ncrC, realistic: ncrR },
      { driver: "Staff time savings", description: "Hours freed from insurance verification, claims, EOB posting, patient comms, recalls, and scheduling", assumptions: `${fteLow}-${fteHigh} FTE equivalent time saved across ${patients} patients/mo`, conservative: staffC, realistic: staffR },
      { driver: "Software expenses eliminated", description: "Replaces 8+ point solutions: PMS, comms, marketing, payments, claims, analytics, verification, and more", assumptions: "Weave, Dental Intel, PMS/server, Zuub, billing portals, & more", conservative: swC, realistic: swR },
    ],
    totalConservative: prodC + ncrC + staffC + swC,
    totalRealistic: prodR + ncrR + staffR + swR,
  };
};

const QuoteROISlide = ({ collections, patients, practiceName, roiRows: precomputedRows, roiTotalConservative: precomputedC, roiTotalRealistic: precomputedR }) => {
  const fallback = (!precomputedRows || precomputedRows.length === 0) ? computeFallbackROI(collections, patients) : null;
  const rows = precomputedRows && precomputedRows.length > 0 ? precomputedRows : fallback.rows;
  const totalConservative = precomputedC ?? fallback?.totalConservative ?? 0;
  const totalRealistic = precomputedR ?? fallback?.totalRealistic ?? 0;

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-5 lg:p-6 relative overflow-hidden font-sans">
      {/* Background */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100 rounded-full blur-[120px] opacity-40 translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-50 rounded-full blur-[100px] opacity-40 -translate-x-1/2 translate-y-1/2" />

      <div className="relative z-10 flex flex-col h-full w-full">
        {/* Header */}
        <div className="flex items-start justify-between mb-3 flex-shrink-0">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-2 border border-blue-100">
              <TrendingUp className="w-4 h-4" />
              <span>ROI Potential — {practiceName}</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              So how much ROI can Asha drive for you? Based on our experience working with other practices, <span className="text-blue-600">here's what we think.</span>
            </h2>
          </motion.div>
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] overflow-hidden"
          >
            <Table>
              <TableHeader className="bg-slate-50 sticky top-0">
                <TableRow className="border-b border-slate-200/80">
                  <TableHead className="text-xs uppercase tracking-wider font-bold text-slate-400 py-3.5 px-5 w-[22%]">
                    Value Driver
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-bold text-slate-400 py-3.5 px-5 w-[30%]">
                    Description
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-bold text-slate-400 py-3.5 px-5 w-[20%]">
                    Assumptions
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-bold text-slate-400 py-3.5 px-5 w-[14%] text-right">
                    Conservative
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-bold text-blue-500 py-3.5 px-5 w-[14%] text-right bg-blue-50/50">
                    Realistic
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow
                    key={i}
                    className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                  >
                    <TableCell className="py-4 px-5 text-[14px] font-semibold text-slate-900 align-top">
                      {row.driver}
                    </TableCell>
                    <TableCell className="py-4 px-5 text-[13px] text-slate-500 align-top leading-relaxed">
                      {row.description}
                    </TableCell>
                    <TableCell className="py-4 px-5 text-[13px] text-slate-400 align-top leading-relaxed">
                      {row.assumptions}
                    </TableCell>
                    <TableCell className="py-4 px-5 text-lg font-bold text-slate-700 text-right align-top tabular-nums">
                      ${row.conservative.toLocaleString()}
                    </TableCell>
                    <TableCell className="py-4 px-5 text-lg font-extrabold text-blue-600 text-right align-top tabular-nums bg-blue-50/30">
                      ${row.realistic.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total Row */}
                <TableRow className="bg-[#0f172a] hover:bg-[#0f172a]">
                  <TableCell className="py-4 px-5 text-base font-extrabold text-white tracking-wide" colSpan={3}>
                    Total Estimated Monthly ROI
                  </TableCell>
                  <TableCell className="py-4 px-5 text-xl font-extrabold text-white text-right tabular-nums">
                    ${totalConservative.toLocaleString()}
                  </TableCell>
                  <TableCell className="py-4 px-5 text-xl font-extrabold text-blue-400 text-right tabular-nums">
                    ${totalRealistic.toLocaleString()}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </motion.div>
          <p className="text-[10px] text-slate-400 mt-1.5 flex-shrink-0 leading-relaxed">
            Disclaimer: These figures are illustrative estimates only and do not constitute a guarantee of results. Actual outcomes vary based on practice size, patient mix, payer contracts, staff engagement, and other factors.
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuoteROISlide;
